import { createContext, useContext, useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import NotificationBridge from "@/components/NotificationBridge";
import LandingPage from "@/pages/LandingPage";
import AuthCallback from "@/pages/AuthCallback";
import ChatPage from "@/pages/ChatPage";
import TarotPage from "@/pages/TarotPage";
import SavedCharts from "@/pages/SavedCharts";
import ProfilePage from "@/pages/ProfilePage";
import UpgradePage from "@/pages/UpgradePage";
import OnboardingScreen from "@/pages/OnboardingScreen";
import AdminPage from "@/pages/AdminPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import { Sparkles, BookmarkCheck, MessageCircle, Globe, LogOut, Loader2, User, Home, CreditCard, Coins, Layers, Crown } from "lucide-react";
import StarField from "@/components/StarField";
import MusicToggle from "@/components/MusicToggle";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const LANGUAGES = [
  "English", "Hindi", "Tamil", "Telugu", "Kannada", "Malayalam", "Bengali",
  "Gujarati", "Marathi", "Punjabi", "Urdu", "Odia", "Assamese", "Sanskrit",
  "Spanish", "French", "German", "Portuguese", "Italian", "Russian",
  "Arabic", "Chinese", "Japanese", "Korean", "Indonesian", "Thai", "Vietnamese",
  "Turkish", "Dutch", "Polish", "Swedish", "Greek", "Hebrew", "Swahili",
];

const LangContext = createContext({ language: "English", setLanguage: () => {} });
export const useLang = () => useContext(LangContext);

function AppNavBar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { language, setLanguage } = useLang();
  const isChat = location.pathname === "/dashboard";
  const [readingsInfo, setReadingsInfo] = useState(null);

  useEffect(() => {
    if (!user) return;
    const fetchCredits = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/user/readings`);
        if (res.data.success) setReadingsInfo(res.data);
      } catch {}
    };
    fetchCredits();
    const interval = setInterval(fetchCredits, 30000);
    return () => clearInterval(interval);
  }, [user]);

  if (!user) return null;

  const totalCredits = readingsInfo?.total_available ?? "-";
  const freeUsed = readingsInfo?.free_available === 0;
  const nextFreeIn = freeUsed ? readingsInfo?.next_free_in || "" : "";

  return (
    <header className="app-header h-14 flex items-center justify-between px-4 sm:px-6 glass-heavy border-b border-white/5 sticky top-0 z-50" data-testid="navbar">
      <Link to="/dashboard" className="flex items-center gap-2 group" data-testid="logo-link">
        <Sparkles className="h-5 w-5 text-amber-400" />
        <span className="font-cinzel text-base sm:text-lg font-bold tracking-wider text-amber-400">Tara Kundli</span>
      </Link>
      <div className="flex items-center gap-1">
        <Link to="/upgrade" className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/10 border border-amber-500/20 text-amber-400 hover:bg-amber-500/15 transition-all" data-testid="credits-badge" title={freeUsed ? `Next free reading ${nextFreeIn}` : `${readingsInfo?.free_available ?? 3} free readings left today`}>
          <Coins className="h-3.5 w-3.5" />
          <span className="font-bold">{totalCredits}</span>
          {freeUsed && nextFreeIn && <span className="text-[9px] text-amber-400/60 hidden sm:inline">({nextFreeIn})</span>}
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-amber-400 gap-1 text-xs px-2" data-testid="lang-selector">
              <Globe className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{language}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-white/10 max-h-80 overflow-hidden">
            <ScrollArea className="h-72">
              {LANGUAGES.map((l) => (
                <DropdownMenuItem key={l} onClick={() => setLanguage(l)}
                  className={`text-xs cursor-pointer ${l === language ? "text-amber-400 bg-amber-500/10" : "text-slate-300"}`}>
                  {l}
                </DropdownMenuItem>
              ))}
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>
        <MusicToggle />
        <Link to="/dashboard" className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all text-slate-400 hover:text-amber-400 hover:bg-amber-500/10" data-testid="nav-home">
          <Home className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Home</span>
        </Link>
        <Link to="/saved" className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all text-slate-400 hover:text-slate-200" data-testid="nav-saved">
          <BookmarkCheck className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Saved</span>
        </Link>
        <Link to="/profile" className="hidden md:block text-slate-400 hover:text-amber-400 p-1.5 rounded-full hover:bg-white/5 transition-all" data-testid="nav-profile" title="Profile">
          {user.picture ? <img src={user.picture} alt="" className="h-6 w-6 rounded-full border border-amber-500/30" /> : <User className="h-4 w-4" />}
        </Link>
      </div>
    </header>
  );
}

const TABS = [
  { to: "/dashboard", label: "Home", icon: Home },
  { to: "/tarot", label: "Tarot", icon: Layers },
  { to: "/saved", label: "Saved", icon: BookmarkCheck },
  { to: "/upgrade", label: "Plans", icon: Crown },
  { to: "/profile", label: "Profile", icon: User },
];

// Phone-style tab bar along the bottom on small screens and in the app.
function BottomTabBar() {
  const location = useLocation();
  const { user } = useAuth();
  const show = !!user && TABS.some((t) => t.to === location.pathname);

  useEffect(() => {
    document.body.classList.toggle("has-tabbar", show);
    return () => document.body.classList.remove("has-tabbar");
  }, [show]);

  if (!show) return null;
  const activeIndex = TABS.findIndex((t) => t.to === location.pathname);

  return (
    <nav className="tab-bar md:hidden" data-testid="bottom-tab-bar">
      <div className="tab-bar-inner">
        <span className="tab-bar-pill" style={{ transform: `translateX(${activeIndex * 100}%)` }} aria-hidden="true" />
        {TABS.map(({ to, label, icon: Icon }, i) => {
          const active = i === activeIndex;
          return (
            <Link key={to} to={to} replace className={`tab-bar-item ${active ? "is-active" : ""}`}
              aria-current={active ? "page" : undefined} data-testid={`tab-${label.toLowerCase()}`}>
              <Icon className="h-5 w-5" strokeWidth={active ? 2.2 : 1.8} />
              <span>{label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading, setUserData } = useAuth();
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="h-8 w-8 text-amber-400 animate-spin" /></div>;
  if (!user) { navigate("/"); return null; }

  // Show onboarding if user hasn't been onboarded
  if (!user.onboarded && !showOnboarding) {
    return <OnboardingScreen onComplete={() => setShowOnboarding(false)} />;
  }

  return children;
}

// Web page that hands the Emergent session_id back to the native app.
function AppAuthBridge({ hash }) {
  const appUrl = `com.tarakundli.app://auth${hash || ""}`;
  useEffect(() => { window.location.href = appUrl; }, [appUrl]);
  return (
    <div className="min-h-screen bg-[#020617] flex flex-col items-center justify-center gap-5 p-6 text-center" data-testid="app-auth-bridge">
      <p className="font-cinzel text-amber-400 text-xl tracking-wide">Signed in</p>
      <p className="text-sm text-slate-400">Returning you to the Tara Kundli app…</p>
      <a href={appUrl} className="rounded-full border border-amber-500/40 px-6 py-2.5 text-sm text-amber-300 hover:bg-amber-500/10 transition-colors" data-testid="app-auth-return-btn">
        Open Tara Kundli
      </a>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  const navigate = useNavigate();

  if (location.pathname === "/app-auth") {
    return <AppAuthBridge hash={location.hash} />;
  }

  // Check URL fragment for session_id SYNCHRONOUSLY (before ProtectedRoute runs)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <div key={location.pathname} className="page-enter">
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/tarot" element={<ProtectedRoute><TarotPage /></ProtectedRoute>} />
      <Route path="/upgrade" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
      <Route path="/saved" element={<ProtectedRoute><SavedCharts onOpenSession={(s) => navigate("/dashboard", { state: { restoreSession: s } })} /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
    </Routes>
    </div>
  );
}

function AppContent() {
  return (
    <>
      <StarField />
      <NotificationBridge />
      <AppNavBar />
      <main className="app-main flex-1"><AppRoutes /></main>
      <BottomTabBar />
    </>
  );
}

function App() {
  const [language, setLanguage] = useState("English");
  return (
    <LangContext.Provider value={{ language, setLanguage }}>
      <AuthProvider>
        <BrowserRouter>
          <div className="min-h-screen flex flex-col">
            <AppContent />
            <Toaster position="top-center" richColors offset="calc(env(safe-area-inset-top) + 64px)" />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </LangContext.Provider>
  );
}

export default App;
